import styled from '@emotion/styled';
import { forwardRef, ReactNode } from 'react';
import { globalPadding } from '../../Layout/Layout';
import { mainBg } from '../../styles/Main';
import { deepMerge } from '../../utils';
import { BreakPointsOnly } from '../../theme';
import ContentPlayer from './ContentPlayer';
import { TeaserDataType } from './ContentHeader';

export const ratio = {
  xs: 1 / 1.4,
  md: 1 / 1.6,
};

export const containerOffset: BreakPointsOnly = {
  xs: `${(64 - 24) / 16}rem`,
  lg: `-${(64 + 24) / 16}rem`,
  xl: `-${140 / 16}rem`,
};

export const contentBottom: BreakPointsOnly = {
  lg: '50%',
  md: '0',
};

export const contentMargin: BreakPointsOnly = {
  sm: '-16rem',
  md: '0',
};

export const Container = styled.section<{ customContentOffset?: BreakPointsOnly }>(({ theme, customContentOffset = containerOffset }) => {
  const styles = {
    color: theme.palette.foreground[900][0],
    backgroundColor: theme.palette.background[mainBg][0],
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-end',
    },
  };

  const responsiveOffset = theme.mixins.create(customContentOffset, (p) => ({ marginBottom: p }));

  return deepMerge(styles, responsiveOffset);
});

export const Content = styled.div<{
  customContentOffset?: BreakPointsOnly,
  responsiveMargin?: BreakPointsOnly
}>(({ theme, customContentOffset = contentBottom,
  responsiveMargin = contentMargin }) => {
  const styles = {
    width: '100%',
    marginTop: '-1.75rem',
    position: 'relative',
    zIndex: 2,
    pointerEvents: 'none',
    padding: '0 1rem',
    '@media (min-width: 600px)': { padding: '0 2rem' },
    '@media (min-width: 960px)': { padding: '0 3.5rem' },
    '@media (min-width: 1920px)': { padding: '0 8rem' },
    'a, button, input, select, textarea, [role="button"], [tabindex]': {
      pointerEvents: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      right: 0,
    },
    [theme.breakpoints.up('lg')]: {
      transform: 'translateY(50%)',
    },
  };

  const responsivePadding = theme.mixins.create(globalPadding, (p) => ({
    padding: `0 ${theme.spacings[p]}rem`,
  }));

  const responsiveBottom = theme.mixins.create(customContentOffset, (p) => ({ bottom: p }));
  const responsiveMarginTop = theme.mixins.create(responsiveMargin, (p) => ({ marginTop: p }));

  return deepMerge(styles, responsivePadding, responsiveBottom, responsiveMarginTop);
});

export  const CoverWrapper = styled.div(({ theme }) => ({
  height: '100%',
  background: 'no-repeat center url(/static/images/placeholder/cover-single.svg)',
  backgroundSize: 'cover',
  [theme.breakpoints.up('md')]: {
    width: '85%',
  },
  [theme.breakpoints.up('lg')]: {
    width: '74%',
  },
}));

// export const CoverImage = styled.div(({ theme }) => ({
//   width: '100%',
//   paddingTop: `${ratio.xs * 100}%`,
//   position: 'relative',
//   zIndex: 0,
//   backgroundRepeat: 'no-repeat',
//   backgroundPosition: 'center',
//   backgroundSize: 'cover',
//   [theme.breakpoints.up('md')]: {
//     paddingTop: `${ratio.md * 100}%`,
//     top: 0,
//     left: 0,
//   },
// }));

export const CoverOverlay = styled.div(({ theme }) => {
  const styles = {
    width: '100%',
    position: 'absolute',
    bottom: 'calc(100% + 1.5rem)',
    right: 0,
    [theme.breakpoints.up('sm')]: {
      width: 'max-content',
    },
    [theme.breakpoints.up('md')]: {
      zIndex: 3,
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 'auto',
      top: 'auto',
    },
  };

  const responsivePadding = theme.mixins.create(globalPadding, (p) => ({
    padding: `0 ${theme.spacings[p]}rem`,
  }));

  return deepMerge(styles, responsivePadding);
});

export type HeaderCoverProps = {
  image?: string;
  teaserData: TeaserDataType;
  children?: ReactNode;
  overlay?: ReactNode;
  containerOffset?: BreakPointsOnly;
  contentOffset?: BreakPointsOnly;
  responsiveMargin?: BreakPointsOnly;
  /** Override the cover height (forwarded to ContentPlayer). Defaults to the aspect-ratio sizing. */
  coverHeight?: string;
};

const ContentHeaderCover = forwardRef<HTMLDivElement, HeaderCoverProps>(function ContentHeaderCover({ teaserData, children, overlay,
  containerOffset, contentOffset, responsiveMargin, coverHeight }, ref) {
  // const imgUrl = useResponsiveUrl({ url: image, desktop: 1920, mobile: 880 });

  return (
    <Container className="header-cover" ref={ref} customContentOffset={containerOffset} >
      <CoverWrapper>
        <ContentPlayer teaserData={teaserData} fixedHeight={coverHeight} />
        {/*<CoverImage style={{ backgroundImage: `url(${imgUrl})` }}>*/}
        {/*  <GradientX />*/}
        {/*  <GradientY />*/}
        {/*</CoverImage>*/}
      </CoverWrapper>
      {/* Without content (the compact teaser) the box would still pull the next element up by its
          negative top margin below md, onto the cover. */}
      {(overlay || children) && (
        <Content customContentOffset={contentOffset}
          responsiveMargin={responsiveMargin}>
          {overlay && <CoverOverlay>{overlay}</CoverOverlay>}
          {children}
        </Content>
      )}
    </Container>
  );
});

export default ContentHeaderCover;
